import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, useCart, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Search,
  ShoppingCart,
  User,
  Menu,
  Mic,
  MicOff,
  X,
  ChevronDown,
  Calculator,
  GitCompare,
  LogOut,
  Package,
  Phone,
} from "lucide-react";

const LOGO_DARK_BG = "https://customer-assets.emergentagent.com/job_6f589b7c-cf0b-4735-b26a-4f9ad45f8238/artifacts/3skh2mwe_Logo_Para_Aplicar_sobre_Fondo_Oscuro.png.png";

export default function Header() {
  const { user, login, logout } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suggestions, setSuggestions] = useState({ products: [], categories: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      fetchSuggestions();
    } else {
      setSuggestions({ products: [], categories: [] });
    }
  }, [searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await api.get("/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await api.get(`/search/suggestions?q=${encodeURIComponent(searchQuery)}`);
      setSuggestions(response.data);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/tienda?search=${encodeURIComponent(searchQuery)}`);
      setShowSuggestions(false);
      setSearchQuery("");
    }
  };

  const startVoiceSearch = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert("Tu navegador no soporta búsqueda por voz");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.lang = 'es-ES';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      setTimeout(() => {
        navigate(`/tienda?search=${encodeURIComponent(transcript)}`);
      }, 500);
    };

    recognition.start();
  };

  return (
    <header className="sticky top-0 z-40" data-testid="main-header">
      <div className="bg-primary text-white">
        <div className="relative industrial-texture"></div>
      
      {/* Top bar - Desktop - Links only */}
      <div className="hidden md:block border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-end h-10 text-sm gap-6">
            <Link to="/calculadora" className="flex items-center gap-1 hover:text-white/80 transition-colors">
              <Calculator className="w-4 h-4" />
              Calculadora
            </Link>
            <Link to="/comparador" className="flex items-center gap-1 hover:text-white/80 transition-colors">
              <GitCompare className="w-4 h-4" />
              Comparador
            </Link>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">
          {/* Mobile menu button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 bg-primary text-white border-r-0">
              <SheetHeader>
                <SheetTitle className="text-white">
                  <img src={LOGO_DARK_BG} alt="Barraca Maldonado" className="h-10" />
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-8 flex flex-col gap-2">
                <Link
                  to="/tienda"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-3 px-4 rounded hover:bg-white/10 font-heading text-lg"
                >
                  VER TODO
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.category_id}
                    to={`/tienda?category=${cat.category_id}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="py-3 px-4 rounded hover:bg-white/10"
                  >
                    {cat.name}
                  </Link>
                ))}
                <div className="border-t border-white/20 my-4"></div>
                <Link
                  to="/calculadora"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-3 px-4 rounded hover:bg-white/10 flex items-center gap-2"
                >
                  <Calculator className="w-5 h-5" />
                  Calculadora de Materiales
                </Link>
                <Link
                  to="/comparador"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-3 px-4 rounded hover:bg-white/10 flex items-center gap-2"
                >
                  <GitCompare className="w-5 h-5" />
                  Comparador
                </Link>
              </nav>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img
              src={LOGO_DARK_BG}
              alt="Barraca Maldonado"
              className="h-10 md:h-14 w-auto"
            />
          </Link>

          {/* Search bar - Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl relative">
            <div className="relative w-full flex gap-2">
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder="¿Qué estás buscando?"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className="w-full h-12 pl-4 pr-12 bg-white text-foreground rounded-sm border-0 focus-visible:ring-2 focus-visible:ring-accent"
                  data-testid="search-input"
                />
                <Button
                  type="submit"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                  data-testid="search-submit-btn"
                >
                  <Search className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Voice Search Button - More prominent */}
              <Button
                type="button"
                onClick={startVoiceSearch}
                className={`h-12 px-4 rounded-sm flex items-center gap-2 transition-all ${
                  isListening 
                    ? 'bg-accent text-white animate-pulse' 
                    : 'bg-white text-primary hover:bg-accent hover:text-white'
                }`}
                data-testid="voice-search-btn"
              >
                {isListening ? (
                  <>
                    <Mic className="w-5 h-5" />
                    <span className="text-sm font-medium">Escuchando...</span>
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5" />
                    <span className="text-sm font-medium">Buscar por voz</span>
                  </>
                )}
              </Button>

              {/* Search suggestions */}
              {showSuggestions && (suggestions.products.length > 0 || suggestions.categories.length > 0) && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-sm shadow-lg border border-border z-50 max-h-80 overflow-auto">
                  {suggestions.categories.length > 0 && (
                    <div className="p-2 border-b">
                      <p className="text-xs text-muted-foreground px-2 mb-1">CATEGORÍAS</p>
                      {suggestions.categories.map((cat) => (
                        <button
                          key={cat.category_id}
                          onClick={() => {
                            navigate(`/tienda?category=${cat.category_id}`);
                            setShowSuggestions(false);
                            setSearchQuery("");
                          }}
                          className="w-full text-left px-2 py-2 hover:bg-muted rounded text-foreground"
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {suggestions.products.length > 0 && (
                    <div className="p-2">
                      <p className="text-xs text-muted-foreground px-2 mb-1">PRODUCTOS</p>
                      {suggestions.products.map((prod) => (
                        <button
                          key={prod.product_id}
                          onClick={() => {
                            navigate(`/producto/${prod.product_id}`);
                            setShowSuggestions(false);
                            setSearchQuery("");
                          }}
                          className="w-full text-left px-2 py-2 hover:bg-muted rounded text-foreground"
                        >
                          {prod.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </form>

          {/* Desktop Categories */}
          <nav className="hidden lg:flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-white hover:bg-white/10 font-heading tracking-wide">
                  CATEGORÍAS
                  <ChevronDown className="ml-1 w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/tienda" className="w-full font-semibold">
                    Ver Todos los Productos
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {categories.map((cat) => (
                  <DropdownMenuItem key={cat.category_id} asChild>
                    <Link to={`/tienda?category=${cat.category_id}`} className="w-full">
                      {cat.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Cart */}
            <Link to="/carrito" data-testid="cart-link">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 relative">
                <ShoppingCart className="w-6 h-6" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-accent text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {itemCount}
                  </span>
                )}
              </Button>
            </Link>

            {/* User menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="user-menu-btn">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <User className="w-6 h-6" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="font-semibold">{user.name}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                    {user.status === "pending" && (
                      <span className="inline-flex items-center mt-1 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                        Cuenta pendiente
                      </span>
                    )}
                    {user.status === "rejected" && (
                      <span className="inline-flex items-center mt-1 text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">
                        Cuenta rechazada
                      </span>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/mi-cuenta" className="w-full flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Mi Cuenta
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="text-white hover:bg-white/10 text-sm hidden sm:flex"
                    data-testid="login-btn"
                  >
                    Ingresar
                  </Button>
                </Link>
                <Link to="/registro">
                  <Button
                    className="bg-accent hover:bg-accent/90 text-white text-sm"
                    data-testid="register-btn"
                  >
                    Registrarse
                  </Button>
                </Link>
                <Link to="/login" className="sm:hidden">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10"
                  >
                    <User className="w-6 h-6" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile search bar */}
      <div className="md:hidden px-4 pb-3 bg-primary">
        <form onSubmit={handleSearch} className="relative">
          <Input
            type="text"
            placeholder="Buscar productos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-11 pl-4 pr-20 bg-white text-foreground rounded-sm border-0"
            data-testid="mobile-search-input"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={startVoiceSearch}
              className={`h-8 w-8 ${isListening ? 'text-accent' : 'text-muted-foreground'}`}
            >
              <Mic className="w-5 h-5" />
            </Button>
            <Button type="submit" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
              <Search className="w-5 h-5" />
            </Button>
          </div>
        </form>
      </div>
      </div>
    </header>
  );
}
